/**
 * Create a food of your own, three ways, and the user picks:
 *  - type the label in (no AI anywhere),
 *  - photograph the label (AI reads it, the user checks every number),
 *  - let AI estimate everything (for homemade or unlabeled food; reviewed before saving).
 * Missing nutrients can be filled with AI later from the food's page, only if the user asks.
 */
import { useState, type ReactElement } from 'react';
import { NUTRIENT_BY_KEY, roundNutrient } from '@engine/nutrients';
import { errorMessage } from '../lib/logger';
import type { CompleteNutrientsResponse, Food, FoodInsert, NutrientMap, NutrientSourceMap } from '../lib/types';
import { useApp } from '../state/app';
import { Icon, type IconName } from './Icon';
import { EMPTY_LABEL_FORM, LabelPhotoFlow, LabelReview } from './LabelEntry';
import { Modal, Spinner } from './ui';

type Way = 'choose' | 'typed' | 'photos' | 'ai';

const WAYS: ReadonlyArray<{ way: Exclude<Way, 'choose'>; icon: IconName; title: string; text: string }> = [
  { way: 'typed', icon: 'edit', title: 'Type the label in myself', text: 'Every number exactly as printed. No AI involved.' },
  { way: 'photos', icon: 'camera', title: 'Photograph the label', text: 'AI reads the Nutrition Facts; you check every number before saving.' },
  { way: 'ai', icon: 'sparkle', title: 'Let AI estimate it', text: 'For homemade or unlabeled food. You review the estimate before saving.' },
];

/** The main numbers shown (and editable) after an AI estimate, per serving. */
const AI_SHOWN: readonly string[] = ['energy_kcal', 'protein_g', 'carbs_g', 'fiber_g', 'sugars_g', 'fat_g', 'saturated_g', 'sodium_mg', 'potassium_mg', 'cholesterol_mg'];

export function CreateFoodModal({ onClose, onCreated }: { onClose: () => void; onCreated: (food: Food) => void }): ReactElement {
  const [way, setWay] = useState<Way>('choose');
  const done = (food: Food): void => { onCreated(food); onClose(); };
  const title: string = way === 'choose' ? 'New food' : WAYS.find((w) => w.way === way)!.title;

  return (
    <Modal title={title} onClose={onClose} width={760}>
      {way === 'choose' && (
        <div className="stack" style={{ gap: 12 }}>
          <p className="muted" style={{ margin: 0 }}>How do you want to add it?</p>
          <div className="way-grid">
            {WAYS.map((w) => (
              <button key={w.way} type="button" className="way-card" onClick={() => setWay(w.way)}>
                <span className="way-icon"><Icon name={w.icon} size={22} /></span>
                <b>{w.title}</b>
                <span className="faint">{w.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {way === 'typed' && <LabelReview initial={EMPTY_LABEL_FORM} barcode={null} aiModel={null} via="typed" onSaved={done} onBack={() => setWay('choose')} />}
      {way === 'photos' && <LabelPhotoFlow barcode={null} onSaved={done} onCancel={() => setWay('choose')} />}
      {way === 'ai' && <AiFood onSaved={done} onBack={() => setWay('choose')} />}
    </Modal>
  );
}

function AiFood({ onSaved, onBack }: { onSaved: (food: Food) => void; onBack: () => void }): ReactElement {
  const { source, toast } = useApp();
  const [name, setName] = useState<string>('');
  const [brand, setBrand] = useState<string>('');
  const [details, setDetails] = useState<string>('');
  const [servingLabel, setServingLabel] = useState<string>('1 serving');
  const [servingGrams, setServingGrams] = useState<string>('');
  const [result, setResult] = useState<CompleteNutrientsResponse | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'estimate' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const g: number = Number(servingGrams);
  const gramsOk: boolean = Number.isFinite(g) && g > 0 && g <= 5000;

  const estimate = async (): Promise<void> => {
    setError(null);
    if (name.trim() === '') { setError('Say what the food is, e.g. “homemade beef chili with beans”.'); return; }
    if (!gramsOk) { setError('Enter the serving weight in grams (weigh one serving, or estimate it).'); return; }
    setBusy('estimate');
    try {
      const res: CompleteNutrientsResponse = await source.completeNutrients({
        food: { name: name.trim(), ...(brand.trim() ? { brand: brand.trim() } : {}), ...(details.trim() ? { details: details.trim() } : {}) },
        known: {},
      });
      if (!res.isFood || Object.keys(res.values).length === 0) { setError('The AI didn’t recognise that as a food. Add a clearer name or details.'); return; }
      setResult(res);
      setEdits({});
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const perServing = (key: string): number | undefined => (result?.values[key] === undefined ? undefined : (result.values[key]! * g) / 100);

  const save = async (): Promise<void> => {
    if (!result) return;
    setError(null);
    const nutrients: NutrientMap = { ...result.values };
    const sources: NutrientSourceMap = Object.fromEntries(Object.keys(nutrients).map((k) => [k, 'ai'])) as NutrientSourceMap;
    for (const [k, raw] of Object.entries(edits)) {
      if (raw.trim() === '') continue;
      const v: number = Number(raw);
      if (!Number.isFinite(v) || v < 0) { setError(`${NUTRIENT_BY_KEY.get(k)?.name ?? k} must be a number of 0 or more.`); return; }
      nutrients[k] = roundNutrient((v * 100) / g);
      sources[k] = 'user';
    }
    const insert: FoodInsert = {
      source: 'ai', name: name.trim().slice(0, 300), brand: brand.trim() ? brand.trim().slice(0, 120) : null, category: null,
      nutrients, nutrient_sources: sources,
      portions: [{ label: (servingLabel.trim() || '1 serving').slice(0, 120), grams: Math.round(g * 100) / 100 }],
      notes: details.trim() ? `AI estimate from: ${details.trim()}`.slice(0, 2000) : 'AI estimate.',
      ai_model: result.usage?.model ?? null,
    };
    setBusy('save');
    try {
      const food: Food = await source.createFood(insert);
      toast(`Saved “${food.name}” (AI estimate)`);
      onSaved(food);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <label className="field"><span>What is it?</span><input className="input" value={name} maxLength={300} autoFocus onChange={(e) => { setName(e.target.value); setResult(null); }} placeholder="e.g. Grandma’s beef chili with beans" /></label>
        <label className="field"><span>Brand (optional)</span><input className="input" value={brand} maxLength={120} onChange={(e) => { setBrand(e.target.value); setResult(null); }} /></label>
      </div>
      <label className="field"><span>Details (the more, the better)</span>
        <textarea className="input" value={details} maxLength={1500} onChange={(e) => { setDetails(e.target.value); setResult(null); }} placeholder="Ingredients and amounts, how it was cooked, restaurant or recipe name…" />
      </label>
      <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <label className="field"><span>Serving name</span><input className="input" value={servingLabel} maxLength={120} onChange={(e) => setServingLabel(e.target.value)} /></label>
        <label className="field"><span>Serving weight</span><div className="input-suffix"><input className="input" type="number" min={0} step="any" value={servingGrams} onChange={(e) => setServingGrams(e.target.value)} /><em>g</em></div></label>
      </div>

      {result && (
        <div className="stack" style={{ gap: 10 }}>
          <div className="note info">
            <b>AI estimate for {servingLabel || '1 serving'} ({g} g){result.overallConfidence !== null ? `, confidence ${Math.round(result.overallConfidence * 100)}%` : ''}.</b> Change any number you know better; the rest of the {Object.keys(result.values).length} nutrients are kept as estimated.
            {(result.assumptions.length > 0 || result.warnings.length > 0) && <ul>{[...result.assumptions, ...result.warnings].map((a) => <li key={a}>{a}</li>)}</ul>}
          </div>
          <div className="form-grid label-grid">
            {AI_SHOWN.filter((k) => result.values[k] !== undefined).map((k) => {
              const def = NUTRIENT_BY_KEY.get(k)!;
              const est: number = perServing(k) ?? 0;
              return (
                <label key={k} className="field"><span>{def.name}</span>
                  <div className="input-suffix"><input className="input" inputMode="decimal" value={edits[k] ?? String(Math.round(est * (def.unit === 'g' ? 10 : 1)) / (def.unit === 'g' ? 10 : 1))} onChange={(e) => setEdits((x) => ({ ...x, [k]: e.target.value }))} /><em>{def.unit}</em></div>
                </label>
              );
            })}
          </div>
        </div>
      )}
      {error !== null && <div className="note err">{error}</div>}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onBack} disabled={busy !== null}>Back</button>
        {!result
          ? <button type="button" className="btn btn-ai" onClick={() => void estimate()} disabled={busy !== null}>{busy === 'estimate' ? <Spinner /> : <Icon name="sparkle" size={18} />} {busy === 'estimate' ? 'Estimating… (up to a minute)' : 'Estimate with AI'}</button>
          : <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy !== null}>{busy === 'save' ? <Spinner /> : <Icon name="check" size={18} />} Save to My foods</button>}
      </div>
    </div>
  );
}
